import { queryOptions } from '@tanstack/react-query';
import { api } from '@/api';
import type { PublicPackage, ShareLink } from './types';
import { createPublicPackageClient, PUBLIC_PACKAGE_QUERY_KEY } from './client';

export const { api: publicApi, queryClient: publicQueryClient } = createPublicPackageClient(
  new URL(import.meta.env.VITE_AI_API_URL, window.location.origin).origin,
);

export const shareLinksQuery = (workspaceId: string, packageId: string) =>
  queryOptions({
    queryKey: ['packageLinks', workspaceId, packageId],
    queryFn: async () =>
      (
        await api.get<ShareLink[]>(`v1/work-packages/scope-breakdown/packages/${packageId}/links`, {
          params: { tenderId: workspaceId },
        })
      ).data,
  });

export const publicPackageQuery = (token: string) =>
  queryOptions({
    queryKey: [PUBLIC_PACKAGE_QUERY_KEY, token],
    retry: false,
    staleTime: 'static',
    queryFn: async () => (await publicApi.get<PublicPackage>(`/public/v1/packages/${token}`)).data,
  });

export const publicFileQuery = (token: string, fileId: string, download: boolean) =>
  queryOptions({
    queryKey: ['publicFile', token, fileId, download],
    gcTime: 0,
    staleTime: 0,
    queryFn: async () =>
      (
        await publicApi.get<{ url: string; expiresAt: string }>(
          `/public/v1/packages/${token}/files/${fileId}`,
          { params: { download } },
        )
      ).data,
  });

export const sharingPolicyQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['packageSharingPolicy', organizationId],
    queryFn: async () =>
      (
        await api.get<import('./types').SharingPolicy>(
          `v1/organizations/${organizationId}/package-sharing`,
        )
      ).data,
  });

export const shareActivityQuery = (workspaceId: string, packageId: string, linkId: string) =>
  queryOptions({
    queryKey: ['shareActivity', workspaceId, packageId, linkId],
    queryFn: async () =>
      (
        await api.get<
          {
            id: string;
            kind: string;
            at: string;
            question: string | null;
            fileName: string | null;
            clarificationId: string | null;
          }[]
        >(`v1/work-packages/scope-breakdown/packages/${packageId}/links/${linkId}/activity`, {
          params: { tenderId: workspaceId },
        })
      ).data,
  });
